import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import ShareModal from "@/components/feature/ShareModal";
import Toast from "@/components/feature/Toast";
import DetailBanner from "./components/DetailBanner";
import InfoPanel from "./components/InfoPanel";
import SpecialArticles from "./components/SpecialArticles";
import RelatedSection from "./components/RelatedSection";
import { getEntryById } from "@/utils/content";
import { isFavorite, toggleFavorite } from "@/utils/favorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import { TYPE_LABEL, type ContentType } from "@/types/content";

const TYPE_PATH: Record<ContentType, string> = {
  movie: "/movie",
  tv: "/tv",
  news: "/news",
  special: "/special",
};

export default function DetailPage() {
  const { id } = useParams();
  const item = getEntryById(id);

  const [favorite, setFavorite] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");

  const title = item ? `${item.title} - 影视工厂` : "内容不存在 - 影视工厂";
  const description = item
    ? `${item.summary} 导演：${item.director}，主演：${item.cast}。`
    : "抱歉，你访问的内容不存在或已删除，请返回影视工厂首页继续浏览。";
  const keywords = item
    ? `${item.title},${item.genres.join(",")},影视工厂,影视详情`
    : "影视工厂";

  usePageMeta(title, description, keywords);

  useEffect(() => {
    if (item) setFavorite(isFavorite(item.id));
  }, [item]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!item) {
    return (
      <div className="bg-background-50">
        <div className="mx-auto max-w-page px-4 md:px-6 py-12">
          <EmptyState
            icon="ri-error-warning-line"
            title="内容不存在或已删除"
            description="你访问的影视内容可能已被移除，或链接有误。不妨回到首页看看其他精彩内容。"
          />
        </div>
      </div>
    );
  }

  const isVideo = item.type === "movie" || item.type === "tv";

  const handleToggleFav = () => {
    const added = toggleFavorite(item.id);
    setFavorite(added);
    setToast(added ? "已加入我的收藏" : "已取消收藏");
  };

  return (
    <div className="bg-background-50">
      <div className="mx-auto max-w-page px-4 md:px-6 py-8 md:py-12">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: TYPE_LABEL[item.type], to: TYPE_PATH[item.type] },
            { label: item.title },
          ]}
        />

        <div className="mt-5 md:mt-6">
          <DetailBanner item={item} />
        </div>

        <div
          className={`mt-8 md:mt-10 grid grid-cols-1 gap-8 ${
            isVideo ? "lg:grid-cols-[280px_1fr]" : ""
          }`}
        >
          {isVideo && (
            <div className="lg:sticky lg:top-24 h-fit">
              <div className="w-40 md:w-full mx-auto rounded-lg overflow-hidden bg-background-200 aspect-[2/3]">
                <img
                  src={item.image}
                  alt={item.title}
                  title={`${item.title} 影视工厂`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          )}

          <InfoPanel
            item={item}
            isFav={favorite}
            onToggleFav={handleToggleFav}
            onShare={() => setShareOpen(true)}
          />
        </div>

        {item.type === "special" && item.articles && (
          <SpecialArticles articles={item.articles} />
        )}

        <RelatedSection item={item} />
      </div>

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={item.title}
      />
      <Toast message={toast} visible={Boolean(toast)} />
    </div>
  );
}